import { exists } from 'base/helpers';
import { extractPaletteColor } from 'base/styleguide/color';
import { assertShadeKey } from 'base/styleguide/concept';
import { mapPartialJson } from 'blocks/llm_output_parsers';
import { LLMProxyService } from 'blocks/llm_proxy_service';
import { z } from 'zod';
import { Page, Project, SectionRef } from '../model';
import { unrefSection } from '../model_helpers';
import { ConceptAPI } from '../styleguide/concept_store';
import { TaskContext } from './task_log';

const prepareSections = (
  sections: ReadonlyArray<SectionRef>,
  project: Project,
  concept: ConceptAPI,
) =>
  sections
    .map((sref, idx) => {
      const section = unrefSection(sref, project);
      if (section?.element.current == null) return;

      const elementId = section.element.current.id;
      if (elementId == null) return;

      return {
        id: idx + 1,
        section,
        elementId,
        isGlobal: sref.type === 'reference',
        existingScheme: concept.getDesignSchemeByElementId(elementId),
      };
    })
    .filter(exists);

export class PageSchemesAi {
  constructor(
    private readonly llm: LLMProxyService,
    private readonly project: Project,
  ) {}

  async applyDesignSchemesToPage({
    page,
    concept,
    ctx,
  }: {
    page: Page;
    concept: ConceptAPI;
    ctx: TaskContext;
  }) {
    const projectBrief =
      this.project.brief || this.project.homePage.description;

    const schemesByName = new Map(
      concept.designSchemes.map((scheme) => [
        concept.getDesignSchemeName(scheme),
        scheme,
      ]),
    );

    const schemesJson = [...schemesByName.entries()].map(([name, scheme]) => ({
      name,
      color: extractPaletteColor(concept, assertShadeKey(scheme.shadeKey)),
    }));

    const sections = prepareSections(page.sections, this.project, concept);

    const sectionsJson = sections.map(({ id, section }) => ({
      id,
      title: section.name,
      description: section.description,
    }));

    const globalSectionsJson = sections
      .map(({ id, section, isGlobal, existingScheme }) =>
        isGlobal && existingScheme != null
          ? {
              id,
              title: section.name,
              scheme: concept.getDesignSchemeName(existingScheme),
            }
          : null,
      )
      .filter(exists);

    const homepageSectionsJson =
      page !== this.project.homePage
        ? prepareSections(this.project.homePage.sections, this.project, concept)
            .map(({ section, existingScheme }) =>
              existingScheme != null
                ? {
                    title: section.name,
                    scheme: concept.getDesignSchemeName(existingScheme),
                  }
                : null,
            )
            .filter(exists)
        : undefined;

    for (let tryCount = 0; tryCount < 3; tryCount++) {
      try {
        const completion = this.llm.infer(
          'page-schemes-1',
          {
            projectBrief,
            sectionsJson,
            schemesJson,
            homepageSectionsJson,
            globalSectionsJson:
              globalSectionsJson.length > 0 ? globalSectionsJson : undefined,
          },
          ctx,
        );
        const jsonStream = mapPartialJson(completion);

        let lastError: string | undefined;

        const updatedSectionIds = new Set<number>();

        for await (const partialJson of jsonStream) {
          const { output, error } = parsePartialLlmOutput(partialJson);
          lastError = error;

          try {
            for (const sectionOutput of output.sectionSchemes) {
              const section = sections.find((s) => s.id === sectionOutput.id);
              if (section == null) {
                throw new Error(`Invalid section ID: ${sectionOutput.id}`);
              }

              if (section.isGlobal && section.existingScheme != null) {
                continue;
              }

              const scheme = schemesByName.get(sectionOutput.scheme);
              if (scheme == null) {
                throw new Error(`Invalid scheme: ${sectionOutput.scheme}`);
              }

              if (updatedSectionIds.has(section.id)) {
                continue;
              }

              ctx.buffer.run(() => {
                concept.setDesignSchemeForElementId(section.elementId, scheme);
              });
              updatedSectionIds.add(section.id);
            }
          } catch (e) {
            lastError = `${e}`;
          }
        }

        if (lastError) {
          throw new Error(`Failed to parse output: ${lastError}`);
        }

        // Check that all non-global sections were updated
        const nonGlobalSections = sections.filter((s) => !s.isGlobal);
        if (nonGlobalSections.some((s) => !updatedSectionIds.has(s.id))) {
          throw new Error('Not all non-global sections were updated');
        }

        // Success
        return;
      } catch (e) {
        console.warn(`Failed to apply design schemes on try ${tryCount}: ${e}`);
      }
    }

    throw new Error('Failed to apply design schemes');
  }
}

const outputSchema = z.object({
  sectionSchemes: z.array(
    z.object({
      id: z.number(),
      scheme: z.string(),
    }),
  ),
});
type Output = z.infer<typeof outputSchema>;

const parsePartialLlmOutput = (
  partialJson: unknown,
): { output: Output; error: string | undefined } => {
  const parsed = outputSchema.safeParse(partialJson);
  if (!parsed.success) {
    return { output: { sectionSchemes: [] }, error: parsed.error.message };
  }
  return { output: parsed.data, error: undefined };
};
